/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState, useMemo } from "react";
import CardMenu from "components/card/CardMenu";
import Card from "components/card";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import { MdCheckCircle, MdCancel, MdOutlineError } from "react-icons/md";
import { format } from "date-fns";
import Progress from "components/progress";
import { useGlobalContext } from "context/globalContext";
import { getPromptsAPI, getFilterPromptsAPI } from "Api/systemPromptsAPI";

import Loader from "components/loader/Loader";
import { consoleLog } from "helpers";

const PromptTable = () => {
  const {
    subCurrentRoute,
    setSubCurrentRoute,
    fetchSystemPrompts,
    setFetchSystemPrompts,
  } = useGlobalContext();

  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterTableData, setFilterTableData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);

  const handleDebounceSearchQuery = useMemo(() => {
    let timeoutId;
    return function (value) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setSearchQuery(value);
        setPageIndex(0);
      }, 300);
    };
  }, []);

  const getFilterSysPrompts = useCallback(async () => {
    if (searchQuery === "") return;
    const token = localStorage.getItem("jwtToken");
    const limit = 10;
    setIsLoading(true);
    const response = await getFilterPromptsAPI(
      { searchQuery, pageIndex, limit },

      setIsLoading,
      token
    );
    try {
      if (response) {
        setFilterTableData(response.data);
        setTotalPages(response.totalPages);
        setFetchSystemPrompts(false);
      }
    } catch (error) {
      consoleLog([error, "error"]);
    }
  }, [searchQuery, pageIndex, setFetchSystemPrompts]);

  const getSystemPrompts = useCallback(async () => {
    const token = localStorage.getItem("jwtToken");
    const limit = 10;

    const response = await getPromptsAPI(
      {
        pageIndex,
        limit,
      },
      setIsLoading,
      token
    );
    if (response) {
      console.log(response, "ress");
      setTableData(response.data);
      setTotalPages(response?.pagination?.totalPages || 0);
      setFetchSystemPrompts(false);
    }
  }, [searchQuery, setFetchSystemPrompts, pageIndex]);

  useEffect(() => {
    if (fetchSystemPrompts) {
      if (searchQuery === "") {
        getSystemPrompts();
      } else {
        getFilterSysPrompts();
      }
    }
  }, [fetchSystemPrompts, getSystemPrompts, getFilterSysPrompts]);

  useEffect(() => {
    if (searchQuery === "") {
      getSystemPrompts();
    } else {
      getFilterSysPrompts();
    }
  }, [searchQuery, pageIndex, getSystemPrompts, getFilterSysPrompts]);

  const columnsData = useMemo(
    () => [
      {
        Header: "Key",
        accessor: "key",
      },
      {
        Header: "Category",
        accessor: "category",
      },
      {
        Header: "Version",
        accessor: "version",
      },
      {
        Header: "Status",
        accessor: "status",
      },

      {
        Header: "Comment",
        accessor: "comment",
      },
      {
        Header: "Created At",
        accessor: "createdAt",
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }) => {
          const progressValue = row.original.status === "Completed" ? 100 : 50;

          return (
            <div
              className="flex cursor-pointer space-x-2"
              onClick={() => setSubCurrentRoute(row.original.key)}
            >
              <Progress value={progressValue} />
            </div>
          );
        },
      },
    ],
    []
  );

  const columns = useMemo(() => columnsData, [columnsData]);

  const tableInstance = useTable(
    {
      columns,
      data: searchQuery === "" ? tableData : filterTableData,
      manualPagination: true,
      pageCount: totalPages,
      pageIndex,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    gotoPage,
  } = tableInstance;

  const handlePageChange = (direction) => {
    if (direction === "next" && pageIndex < totalPages - 1) {
      setPageIndex((prevPageIndex) => {
        const newPageIndex = prevPageIndex + 1;
        gotoPage(newPageIndex);
        return newPageIndex;
      });
    }

    if (direction === "previous" && pageIndex > 0) {
      setPageIndex((prevPageIndex) => {
        const newPageIndex = prevPageIndex - 1;
        gotoPage(newPageIndex);
        return newPageIndex;
      });
    }
  };

  return (
    <Card extra={"w-full h-full p-4 sm:overflow-x-auto "}>
      <div className="relative flex items-center justify-between ">
        <div className="text-xl font-bold text-navy-700 dark:text-white ">
          <div className="relative w-full sm:w-64">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 text-lg leading-none text-gray-400">
              🔍
            </span>
            <input
              type="search"
              className="w-full rounded-md border border-gray-300 py-2 pl-8 pr-2 text-sm transition-all duration-300 ease-in-out hover:border-blue-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
              placeholder="Search"
              onChange={(e) => {
                e.preventDefault();
                handleDebounceSearchQuery(e.target.value);
              }}
            />
          </div>
        </div>
        {!subCurrentRoute && <CardMenu />}
      </div>

      <div className="mt-4 h-full overflow-x-scroll xl:overflow-auto">
        {!subCurrentRoute ? (
          <>
            {isLoading &&
              (searchQuery
                ? filterTableData.length === 0
                : tableData.length === 0) && (
                <div className="flex h-[400px] w-full items-center justify-center">
                  <Loader />
                </div>
              )}
            {!isLoading &&
              ((searchQuery && filterTableData.length === 0) ||
                (!searchQuery && tableData.length === 0)) && (
                <span className="block w-full text-center">No data found.</span>
              )}

            {(searchQuery ? filterTableData : tableData).length > 0 && (
              <table {...getTableProps()} className="w-full">
                <thead>
                  {headerGroups.map((headerGroup, index) => (
                    <tr {...headerGroup.getHeaderGroupProps()} key={index}>
                      {headerGroup.headers.map((column, index) => {
                        return (
                          <th
                            key={index}
                            className="border-b border-gray-200 pr-20 pb-[10px] text-start dark:!border-navy-700"
                          >
                            <p className="text-xs tracking-wide text-gray-600">
                              {column.render("Header")}
                            </p>
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.map((row, index) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()} key={index}>
                        {row.cells.map((cell, index) => {
                          let data = cell.render("Cell");

                          if (cell.column.Header === "Status") {
                            data = (
                              <div className="flex items-center">
                                <div className={`rounded-full text-xl`}>
                                  {cell.value === true ? (
                                    <MdCheckCircle className="text-green-500" />
                                  ) : cell.value === false ? (
                                    <MdCancel className="text-red-500" />
                                  ) : cell.value === "Error" ? (
                                    <MdOutlineError className="text-orange-500" />
                                  ) : null}
                                </div>
                              </div>
                            );
                          } else if (cell.column.Header === "Created At") {
                            data = format(
                              new Date(cell?.value || null),
                              "yyyy-MM-dd HH:mm:ss"
                            );
                          } else if (cell.column.Header === "Actions") {
                            data = (
                              <Progress
                                width="w-[68px]"
                                value={cell.value}
                                cell={cell}
                              />
                            );
                          }

                          return (
                            <td
                              className="px-[7px] pt-[14px] pb-[8px] sm:text-[14px]"
                              {...cell.getCellProps()}
                              key={index}
                            >
                              <p
                                className={`max-w-fit ${
                                  cell.column.Header === "Key"
                                }`}
                                // onClick={() => setSubCurrentRoute(cell.value)}
                              >
                                {data}
                              </p>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {/* Pagination Controls */}
            {((!!tableData.length && !searchQuery) ||
              (!!filterTableData.length && searchQuery)) && (
              <div className="mt-4 flex items-center justify-between">
                <button
                  onClick={() => handlePageChange("previous")}
                  disabled={pageIndex === 0}
                  className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
                    pageIndex > 0
                      ? "bg-blue-500 hover:bg-blue-600"
                      : "cursor-not-allowed bg-gray-300"
                  }`}
                >
                  Previous
                </button>

                <span className="text-sm text-gray-700 dark:text-white">
                  Page <strong>{pageIndex + 1}</strong> of{" "}
                  <strong>{totalPages}</strong>
                </span>

                <button
                  onClick={() => handlePageChange("next")}
                  disabled={pageIndex >= totalPages - 1}
                  className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
                    pageIndex < totalPages - 1
                      ? "bg-blue-500 hover:bg-blue-600"
                      : "cursor-not-allowed bg-gray-300"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <div></div>
        )}
      </div>
    </Card>
  );
};

export default PromptTable;
